import { useRef, useEffect, useCallback } from "react";
import { getCenterMostThumbnail } from "../utils/carouselUtils";
import { SCROLL_DIFFERENCE_THRESHOLD } from "../utils/constants";

export default function useMobileCarousel(
  activeIndex,
  setActiveIndex,
  videosLength,
) {
  const mobileCarouselRef = useRef(null);
  const thumbnailRefs = useRef([]);
  const scrollTimeoutRef = useRef(null);

  // Calculate which thumbnail is closest to center
  const getCenterMost = useCallback(() => {
    return getCenterMostThumbnail(
      mobileCarouselRef,
      thumbnailRefs,
      videosLength,
    );
  }, [videosLength]);

  // Handle mobile scroll with immediate active state update
  const handleMobileScroll = useCallback(() => {
    const centerIndex = getCenterMost();
    if (centerIndex !== activeIndex) {
      setActiveIndex(centerIndex);
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      // Could add debounce here if needed
    }, 1);
  }, [getCenterMost, activeIndex, setActiveIndex]);

  // Centralized scrolling logic for mobile
  useEffect(() => {
    if (
      window.innerWidth < 1024 &&
      mobileCarouselRef.current &&
      thumbnailRefs.current[activeIndex]
    ) {
      const container = mobileCarouselRef.current;
      const thumbnail = thumbnailRefs.current[activeIndex];

      if (thumbnail) {
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const containerWidth = containerRect.width;

        let targetScroll;

        // Handle first slide - scroll to beginning
        if (activeIndex === 0) {
          targetScroll = 0;
        }
        // Handle last slide - scroll to end
        else if (activeIndex === videosLength - 1) {
          targetScroll = scrollWidth - containerWidth;
        }
        // Handle middle slides - center them
        else {
          targetScroll =
            scrollLeft +
            thumbnailRect.left -
            containerRect.left -
            (containerRect.width - thumbnailRect.width) / 2;
        }

        // Check if we need to scroll (with tolerance)
        const currentScroll = container.scrollLeft;
        const scrollDifference = Math.abs(currentScroll - targetScroll);

        // Only scroll if there's a significant difference
        if (scrollDifference > SCROLL_DIFFERENCE_THRESHOLD) {
          container.scrollTo({
            left: Math.max(
              0,
              Math.min(targetScroll, scrollWidth - containerWidth),
            ),
            behavior: "smooth",
          });
        }
      }
    }
  }, [activeIndex, videosLength]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    mobileCarouselRef,
    thumbnailRefs,
    handleMobileScroll,
  };
}
